import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Product,
  FarmSummary,
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  listFarms,
  createFarm,
} from "../../services/productService";

type AvailabilityFilter = "all" | "active" | "inactive";
type SortKey = "newest" | "oldest" | "stock_high" | "stock_low" | "price_high" | "price_low";

type ProductFormState = {
  name: string;
  description: string;
  category: string;
  unit: string;
  price_per_unit: string;
  current_stock: string;
  minimum_order_quantity: string;
  farm_id: string;
  is_available: boolean;
  is_featured: boolean;
  variety: string;
  quality_grade: string;
  storage_conditions: string;
};

type SourceFormState = {
  name: string;
  location: string;
  description: string;
};

const CATEGORY_OPTIONS = ["vegetables", "fruits", "grains", "livestock", "dairy", "herbs", "nuts"];
const UNIT_OPTIONS = ["kg", "gram", "piece", "bunch", "bag", "carton"];
const CATEGORY_PRODUCT_NAME_OPTIONS: Record<string, string[]> = {
  vegetables: [
    "Tomatoes",
    "Pepper",
    "Onions",
    "Okra",
    "Ugu Leaves",
    "Cabbage",
    "Carrot",
    "Cucumber",
  ],
  fruits: [
    "Banana",
    "Plantain",
    "Orange",
    "Pineapple",
    "Mango",
    "Watermelon",
    "Papaya",
    "Avocado",
  ],
  grains: [
    "Rice",
    "Maize",
    "Beans",
    "Millet",
    "Sorghum",
    "Wheat",
    "Ofada Rice",
    "Brown Beans",
  ],
  livestock: [
    "Chicken",
    "Turkey",
    "Goat Meat",
    "Beef",
    "Catfish",
    "Tilapia",
    "Eggs",
  ],
  dairy: [
    "Fresh Milk",
    "Yoghurt",
    "Cheese",
    "Butter",
    "Cream",
  ],
  herbs: [
    "Basil",
    "Parsley",
    "Scent Leaf",
    "Thyme",
    "Rosemary",
    "Mint",
  ],
  nuts: [
    "Groundnut",
    "Cashew Nuts",
    "Coconut",
    "Walnut",
    "Tiger Nuts",
  ],
};

const EMPTY_FORM: ProductFormState = {
  name: "",
  description: "",
  category: "vegetables",
  unit: "kg",
  price_per_unit: "",
  current_stock: "0",
  minimum_order_quantity: "1",
  farm_id: "",
  is_available: true,
  is_featured: false,
  variety: "",
  quality_grade: "",
  storage_conditions: "",
};

const EMPTY_SOURCE_FORM: SourceFormState = {
  name: "",
  location: "",
  description: "",
};

const parseJson = <T,>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getTenantId = () => {
  const tenant = parseJson<{ id?: string }>(localStorage.getItem("currentTenant"));
  const userData = parseJson<{ tenant_id?: string }>(localStorage.getItem("user_data"));
  const user = parseJson<{ tenant_id?: string }>(localStorage.getItem("user"));
  return tenant?.id || userData?.tenant_id || user?.tenant_id || "";
};

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 0 }).format(
    Number(value || 0)
  );

const formatDate = (value?: string) => {
  if (!value) return "-";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? value : dt.toLocaleString();
};

const AdminProducts: React.FC = () => {
  const { hasCapability } = useAuth();
  const canAccess = hasCapability("capability:admin_console");
  const tenantId = useMemo(() => getTenantId(), []);

  const [products, setProducts] = useState<Product[]>([]);
  const [farms, setFarms] = useState<FarmSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [busyProductId, setBusyProductId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [form, setForm] = useState<ProductFormState>(EMPTY_FORM);
  const [sourceMode, setSourceMode] = useState<"existing" | "new">("existing");
  const [newSourceForm, setNewSourceForm] = useState<SourceFormState>(EMPTY_SOURCE_FORM);

  const loadData = async (isRefresh = false) => {
    if (!canAccess) return;
    if (!tenantId) {
      setError("Tenant context not found. Re-login to reload tenant session.");
      setLoading(false);
      return;
    }
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      setError(null);
      const [productsData, farmsData] = await Promise.all([
        fetchProducts(tenantId, { limit: 500, sort_by: "created_at", sort_order: "desc" }),
        listFarms(tenantId).catch(() => [] as FarmSummary[]),
      ]);
      setProducts(productsData);
      setFarms(farmsData);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Failed to load products";
      setError(err?.response?.status === 401 ? "Invalid authentication credentials. Please log in again." : detail);
    } finally {
      if (isRefresh) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess) return;
    loadData();
  }, [canAccess, tenantId]);

  useEffect(() => {
    if (!selectedProductId && products.length > 0) {
      setSelectedProductId(products[0].id);
      return;
    }
    if (selectedProductId && !products.some((p) => p.id === selectedProductId)) {
      setSelectedProductId(products[0]?.id || null);
    }
  }, [products, selectedProductId]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = products.filter((p) => {
      const availOk =
        availabilityFilter === "all"
          ? true
          : availabilityFilter === "active"
          ? p.is_available
          : !p.is_available;
      const categoryOk = categoryFilter === "all" ? true : (p.category || "").toLowerCase() === categoryFilter;
      const queryOk =
        !q ||
        (p.name || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q) ||
        (p.seller_name || "").toLowerCase().includes(q) ||
        (p.id || "").toLowerCase().includes(q) ||
        (p.quality_grade || "").toLowerCase().includes(q);
      return availOk && categoryOk && queryOk;
    });

    const time = (p: Product) => new Date(p.created_at || 0).getTime() || 0;
    return [...list].sort((a, b) => {
      if (sortKey === "oldest") return time(a) - time(b);
      if (sortKey === "stock_high") return (b.current_stock || 0) - (a.current_stock || 0);
      if (sortKey === "stock_low") return (a.current_stock || 0) - (b.current_stock || 0);
      if (sortKey === "price_high") return (b.price_per_unit || 0) - (a.price_per_unit || 0);
      if (sortKey === "price_low") return (a.price_per_unit || 0) - (b.price_per_unit || 0);
      return time(b) - time(a);
    });
  }, [products, search, availabilityFilter, categoryFilter, sortKey]);

  const selectedProduct =
    filteredProducts.find((p) => p.id === selectedProductId) ||
    products.find((p) => p.id === selectedProductId) ||
    null;

  const metrics = useMemo(() => {
    const total = products.length;
    const active = products.filter((p) => p.is_available).length;
    const inactive = total - active;
    const lowStock = products.filter((p) => (p.current_stock || 0) <= 20 && p.is_available).length;
    const featured = products.filter((p) => p.is_featured).length;
    const inventoryUnits = products.reduce((sum, p) => sum + Number(p.current_stock || 0), 0);
    return { total, active, inactive, lowStock, featured, inventoryUnits };
  }, [products]);

  const farmNameById = useMemo(
    () => Object.fromEntries(farms.map((f) => [f.id, f.name])),
    [farms]
  );
  const categoryNameOptions = useMemo(
    () => CATEGORY_PRODUCT_NAME_OPTIONS[form.category] || [],
    [form.category]
  );

  const openCreateForm = () => {
    setEditingProduct(null);
    setForm({
      ...EMPTY_FORM,
      farm_id: farms[0]?.id || "",
    });
    setSourceMode(farms.length > 0 ? "existing" : "new");
    setNewSourceForm({
      ...EMPTY_SOURCE_FORM,
      name: farms.length === 0 ? "Open Market" : "",
    });
    setShowForm(true);
  };

  const openEditForm = (product: Product) => {
    setEditingProduct(product);
    setForm({
      name: product.name || "",
      description: product.description || "",
      category: (product.category || "vegetables").toLowerCase(),
      unit: (product.unit || "kg").toLowerCase(),
      price_per_unit: String(product.price_per_unit ?? ""),
      current_stock: String(product.current_stock ?? 0),
      minimum_order_quantity: String(product.minimum_order_quantity ?? 1),
      farm_id: product.farm_id || farms[0]?.id || "",
      is_available: Boolean(product.is_available),
      is_featured: Boolean(product.is_featured),
      variety: (product as any).variety || "",
      quality_grade: product.quality_grade || "",
      storage_conditions: (product as any).storage_conditions || "",
    });
    setSourceMode("existing");
    setNewSourceForm(EMPTY_SOURCE_FORM);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setForm(EMPTY_FORM);
    setSourceMode("existing");
    setNewSourceForm(EMPTY_SOURCE_FORM);
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) {
      toast.error("Tenant context missing");
      return;
    }
    if (!form.name.trim()) return toast.error("Product name is required");
    let resolvedSourceId = form.farm_id;
    if (sourceMode === "existing" && !resolvedSourceId) {
      return toast.error("Select a source before creating a product");
    }
    if (sourceMode === "new" && !newSourceForm.name.trim()) {
      return toast.error("Source name is required");
    }
    const price = Number(form.price_per_unit);
    const stock = Number(form.current_stock);
    const minQty = Number(form.minimum_order_quantity);
    if (!(price > 0)) return toast.error("Price must be greater than 0");
    if (stock < 0) return toast.error("Stock cannot be negative");
    if (minQty <= 0) return toast.error("Minimum order quantity must be greater than 0");

    setFormSubmitting(true);
    try {
      if (sourceMode === "new") {
        const createdSource = await createFarm(tenantId, {
          name: newSourceForm.name.trim(),
          location: newSourceForm.location.trim() || undefined,
          description:
            newSourceForm.description.trim() ||
            "Source created inline from product form (farm / market / supplier).",
        });
        setFarms((prev) => {
          const exists = prev.some((f) => f.id === createdSource.id);
          return exists ? prev : [...prev, createdSource];
        });
        resolvedSourceId = createdSource.id;
        setForm((prev) => ({ ...prev, farm_id: createdSource.id }));
        setSourceMode("existing");
        toast.success("Source created");
      }

      if (editingProduct) {
        const updated = await updateProduct(tenantId, editingProduct.id, {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          category: form.category as any,
          unit: form.unit as any,
          price_per_unit: price,
          current_stock: stock,
          minimum_order_quantity: minQty,
          farm_id: resolvedSourceId,
          is_available: form.is_available,
          is_featured: form.is_featured,
          quality_grade: form.quality_grade.trim() || undefined,
          ...(form.variety.trim() ? { variety: form.variety.trim() } : {}),
          ...(form.storage_conditions.trim() ? { storage_conditions: form.storage_conditions.trim() } : {}),
        } as any);
        setProducts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
        toast.success("Product updated");
      } else {
        const created = await createProduct(tenantId, {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          category: form.category,
          unit: form.unit,
          price_per_unit: price,
          current_stock: stock,
          minimum_order_quantity: minQty,
          farm_id: resolvedSourceId,
          is_available: form.is_available,
          is_featured: form.is_featured,
          variety: form.variety.trim() || undefined,
          quality_grade: form.quality_grade.trim() || undefined,
          storage_conditions: form.storage_conditions.trim() || undefined,
        });
        setProducts((prev) => [created, ...prev]);
        setSelectedProductId(created.id);
        toast.success("Product created");
      }
      closeForm();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to save product");
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleToggleAvailability = async (product: Product) => {
    if (!tenantId) return;
    setBusyProductId(product.id);
    try {
      const updated = await updateProduct(tenantId, product.id, { is_available: !product.is_available });
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, ...updated } : p)));
      toast.success(product.is_available ? "Product deactivated" : "Product activated");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to update product");
    } finally {
      setBusyProductId(null);
    }
  };

  const handleQuickStockAdjust = async (product: Product, delta: number) => {
    if (!tenantId) return;
    const next = Math.max(0, Number(product.current_stock || 0) + delta);
    setBusyProductId(product.id);
    try {
      const updated = await updateProduct(tenantId, product.id, { current_stock: next });
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, ...updated } : p)));
      toast.success("Stock updated");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Stock update failed");
    } finally {
      setBusyProductId(null);
    }
  };

  const handleArchiveProduct = async (product: Product) => {
    if (!tenantId) return;
    if (!window.confirm(`Archive product "${product.name}"? This marks it unavailable.`)) return;
    setBusyProductId(product.id);
    try {
      await deleteProduct(tenantId, product.id);
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, is_available: false } : p))
      );
      toast.success("Product archived");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to archive product");
    } finally {
      setBusyProductId(null);
    }
  };

  if (!canAccess) {
    return (
      <div className="p-8 text-center text-red-600 font-semibold text-lg">
        Access denied. Only admins can manage products.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Product Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Tenant catalog dashboard for product availability, stock and pricing.
            {tenantId ? <span className="ml-2 font-mono text-xs">Tenant: {tenantId}</span> : null}
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/admin/sources"
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            Manage Sources
          </Link>
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
          <button
            onClick={openCreateForm}
            className="px-4 py-2 rounded-lg bg-green-700 text-white font-semibold hover:bg-green-800"
          >
            Add Product
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Products</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{metrics.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Active</div>
          <div className="text-2xl font-bold text-green-700 mt-1">{metrics.active}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Inactive</div>
          <div className="text-2xl font-bold text-gray-700 mt-1">{metrics.inactive}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Low Stock</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{metrics.lowStock}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Featured</div>
          <div className="text-2xl font-bold text-blue-700 mt-1">{metrics.featured}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs text-gray-500">Inventory Units</div>
          <div className="text-2xl font-bold text-indigo-700 mt-1">{metrics.inventoryUnits}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid md:grid-cols-4 gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, category, seller, product ID..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm md:col-span-2"
          />
          <select
            value={availabilityFilter}
            onChange={(e) => setAvailabilityFilter(e.target.value as AvailabilityFilter)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Availability</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Categories</option>
            {CATEGORY_OPTIONS.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="stock_high">Stock High to Low</option>
            <option value="stock_low">Stock Low to High</option>
            <option value="price_high">Price High to Low</option>
            <option value="price_low">Price Low to High</option>
          </select>
          <div className="text-xs text-gray-500 flex items-center">
            Sources available for product assignment: <span className="font-semibold ml-1">{farms.length}</span>
          </div>
        </div>
      </div>

      {loading && <div className="bg-white rounded-xl border border-gray-200 p-6 text-gray-600">Loading products...</div>}

      {!loading && error && (
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <div className="text-red-600 font-semibold mb-2">{error}</div>
          {error.toLowerCase().includes("authentication") && (
            <div className="text-sm text-gray-600">
              Re-login to refresh `auth_token` for this tenant. The products dashboard UI is ready, but protected API calls need a valid session.
            </div>
          )}
        </div>
      )}

      {!loading && !error && filteredProducts.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="text-lg font-semibold text-gray-900 mb-2">No products found</div>
          <div className="text-sm text-gray-600 mb-3">
            {products.length === 0
              ? "This tenant does not have products yet."
              : "No products match your current filters."}
          </div>
          {farms.length === 0 && (
            <div className="text-sm text-amber-700">
              Add a source first. Open <Link to="/admin/sources" className="font-medium underline">Source Registry</Link> to create an "Open Market" source if no specific farm is registered.
            </div>
          )}
        </div>
      )}

      {!loading && !error && filteredProducts.length > 0 && (
        <div className="grid xl:grid-cols-[1.65fr_1fr] gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Product</th>
                    <th className="px-4 py-3 text-left font-semibold">Category</th>
                    <th className="px-4 py-3 text-left font-semibold">Price</th>
                    <th className="px-4 py-3 text-left font-semibold">Stock</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => {
                    const isSelected = selectedProductId === p.id;
                    const rowBusy = busyProductId === p.id;
                    return (
                      <tr
                        key={p.id}
                        className={`border-t border-gray-100 cursor-pointer ${isSelected ? "bg-green-50/40" : "hover:bg-gray-50"}`}
                        onClick={() => setSelectedProductId(p.id)}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{p.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{p.seller_name || farmNameById[p.farm_id || ""] || "No seller"}</div>
                          <div className="text-xs text-gray-400 mt-1 font-mono">{p.id}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="capitalize text-gray-900">{p.category}</div>
                          <div className="text-xs text-gray-500 mt-1 uppercase">{p.unit}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{formatCurrency(p.price_per_unit)}</div>
                          <div className="text-xs text-gray-500 mt-1">MOQ: {p.minimum_order_quantity}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`font-semibold ${(p.current_stock || 0) <= 20 ? "text-amber-700" : "text-gray-900"}`}>
                            {p.current_stock}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{(p.current_stock || 0) <= 20 ? "Low stock" : "Healthy stock"}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-2">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${p.is_available ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                              {p.is_available ? "Active" : "Inactive"}
                            </span>
                            {p.is_featured && (
                              <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold bg-blue-100 text-blue-700">
                                Featured
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => openEditForm(p)}
                              disabled={rowBusy}
                              className="px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleAvailability(p)}
                              disabled={rowBusy}
                              className={`px-3 py-1 rounded-md text-white disabled:opacity-50 ${p.is_available ? "bg-amber-600 hover:bg-amber-700" : "bg-green-600 hover:bg-green-700"}`}
                            >
                              {p.is_available ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              onClick={() => handleArchiveProduct(p)}
                              disabled={rowBusy}
                              className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            >
                              Archive
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 h-fit sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Detail</h2>
            {!selectedProduct ? (
              <div className="text-sm text-gray-600">Select a product to inspect details.</div>
            ) : (
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500">Product</div>
                  <div className="font-semibold text-gray-900 mt-1">{selectedProduct.name}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-500">Price</div>
                    <div className="font-semibold text-green-700 mt-1">{formatCurrency(selectedProduct.price_per_unit)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Stock</div>
                    <div className="font-semibold text-gray-900 mt-1">{selectedProduct.current_stock}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-500">Category</div>
                    <div className="capitalize text-gray-900 mt-1">{selectedProduct.category}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Unit / MOQ</div>
                    <div className="text-gray-900 mt-1 uppercase">
                      {selectedProduct.unit} / {selectedProduct.minimum_order_quantity}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Source</div>
                  <div className="text-gray-900 mt-1">{farmNameById[selectedProduct.farm_id || ""] || selectedProduct.farm_id || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Seller</div>
                  <div className="text-gray-900 mt-1">{selectedProduct.seller_name || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Description</div>
                  <div className="text-gray-900 mt-1 whitespace-pre-wrap">{selectedProduct.description || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Created</div>
                  <div className="text-gray-900 mt-1">{formatDate(selectedProduct.created_at)}</div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-2">Quick Stock Adjust</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleQuickStockAdjust(selectedProduct, -10)}
                      disabled={busyProductId === selectedProduct.id}
                      className="px-3 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      -10
                    </button>
                    <button
                      onClick={() => handleQuickStockAdjust(selectedProduct, +10)}
                      disabled={busyProductId === selectedProduct.id}
                      className="px-3 py-2 rounded-md bg-green-700 text-white hover:bg-green-800 disabled:opacity-50"
                    >
                      +10
                    </button>
                    <button
                      onClick={() => openEditForm(selectedProduct)}
                      className="ml-auto px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Edit Product
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-gray-200">
            <form onSubmit={handleSaveProduct}>
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingProduct ? "Edit Product" : "Add Product"}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {editingProduct
                      ? "Update catalog details, price, stock and availability."
                      : "Create a new tenant product. A source selection is required by the backend (farm or open market source)."}
                  </p>
                </div>
                <button type="button" onClick={closeForm} className="text-gray-500 hover:text-gray-700">
                  Close
                </button>
              </div>

              <div className="p-6 grid md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input
                    list={`product-name-suggestions-${form.category}`}
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder={categoryNameOptions[0] ? `e.g. ${categoryNameOptions[0]}` : "Enter product name"}
                    required
                  />
                  <datalist id={`product-name-suggestions-${form.category}`}>
                    {categoryNameOptions.map((option) => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                  {categoryNameOptions.length > 0 && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Quick pick from {form.category}
                      </label>
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          if (!e.target.value) return;
                          setForm((prev) => ({ ...prev, name: e.target.value }));
                          e.target.value = "";
                        }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                      >
                        <option value="">Select suggested product...</option>
                        {categoryNameOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Source (Farm / Market / Supplier)</label>
                  {!editingProduct && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setSourceMode("existing")}
                        className={`px-3 py-1.5 rounded-lg text-sm border ${
                          sourceMode === "existing"
                            ? "bg-green-50 border-green-300 text-green-700"
                            : "bg-white border-gray-300 text-gray-700"
                        }`}
                      >
                        Select Existing Source
                      </button>
                      <button
                        type="button"
                        onClick={() => setSourceMode("new")}
                        className={`px-3 py-1.5 rounded-lg text-sm border ${
                          sourceMode === "new"
                            ? "bg-blue-50 border-blue-300 text-blue-700"
                            : "bg-white border-gray-300 text-gray-700"
                        }`}
                      >
                        Enter New Source
                      </button>
                    </div>
                  )}

                  {(editingProduct || sourceMode === "existing") && (
                    <div>
                      <select
                        value={form.farm_id}
                        onChange={(e) => setForm((prev) => ({ ...prev, farm_id: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                        required
                      >
                        <option value="">Select source</option>
                        {farms.map((farm) => (
                          <option key={farm.id} value={farm.id}>
                            {farm.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        If the product was bought from an open market, create/select a source like "Open Market".
                      </p>
                    </div>
                  )}

                  {!editingProduct && sourceMode === "new" && (
                    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-gray-900">New Source Details</div>
                        <button
                          type="button"
                          onClick={() =>
                            setNewSourceForm((prev) => ({
                              ...prev,
                              name: prev.name || "Open Market",
                              description:
                                prev.description ||
                                "General open market / unregistered source captured during product creation.",
                            }))
                          }
                          className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        >
                          Use Open Market Template
                        </button>
                      </div>
                      <div className="grid md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Source Name</label>
                          <input
                            value={newSourceForm.name}
                            onChange={(e) => setNewSourceForm((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="Open Market - Lagos"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                            required={sourceMode === "new"}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                          <input
                            value={newSourceForm.location}
                            onChange={(e) => setNewSourceForm((prev) => ({ ...prev, location: e.target.value }))}
                            placeholder="Mile 12, Lagos"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                          <input
                            value={newSourceForm.description}
                            onChange={(e) => setNewSourceForm((prev) => ({ ...prev, description: e.target.value }))}
                            placeholder="Supplier / market source notes"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">
                        On submit, the system will create this source first, then create the product using that source.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <select
                    value={form.unit}
                    onChange={(e) => setForm((prev) => ({ ...prev, unit: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                  >
                    {UNIT_OPTIONS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price / Unit (NGN)</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={form.price_per_unit}
                    onChange={(e) => setForm((prev) => ({ ...prev, price_per_unit: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
                  <input
                    type="number"
                    min="0"
                    value={form.current_stock}
                    onChange={(e) => setForm((prev) => ({ ...prev, current_stock: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Order Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={form.minimum_order_quantity}
                    onChange={(e) => setForm((prev) => ({ ...prev, minimum_order_quantity: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variety</label>
                  <input
                    value={form.variety}
                    onChange={(e) => setForm((prev) => ({ ...prev, variety: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quality Grade</label>
                  <input
                    value={form.quality_grade}
                    onChange={(e) => setForm((prev) => ({ ...prev, quality_grade: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Storage Conditions</label>
                  <input
                    value={form.storage_conditions}
                    onChange={(e) => setForm((prev) => ({ ...prev, storage_conditions: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.is_available}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_available: e.target.checked }))}
                  />
                  Available for sale
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.is_featured}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_featured: e.target.checked }))}
                  />
                  Featured product
                </label>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="px-4 py-2 rounded-lg bg-green-700 text-white font-semibold hover:bg-green-800 disabled:opacity-50"
                >
                  {formSubmitting ? "Saving..." : editingProduct ? "Save Changes" : "Create Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
